import React, { useState, useMemo, useEffect } from 'react';
import { Mafs, Coordinates, Point, Plot, Theme, Text } from 'mafs';
import { Button, Tooltip, Switch } from 'antd';
import { ReloadOutlined, PlusCircleOutlined } from '@ant-design/icons';

export const ScatterPlot = ({ config = {}, variables = {} }) => {
    const initialPoints = config.dataPoints || [[1, 1.2], [2, 2.5], [3, 2.8], [4, 4.5], [5, 4.8], [6, 6.2], [7, 6.8], [8, 8.5]];
    const [points, setPoints] = useState(initialPoints);
    const [showGhost, setShowGhost] = useState(false);

    useEffect(() => {
        if (config.dataPoints) setPoints(config.dataPoints);
    }, [config.dataPoints]);

    const calculateRegression = (pts) => {
        const n = pts.length;
        if (n < 2) return null;
        const sumX = pts.reduce((a, p) => a + p[0], 0);
        const sumY = pts.reduce((a, p) => a + p[1], 0);
        const sumXY = pts.reduce((a, p) => a + p[0] * p[1], 0);
        const sumXX = pts.reduce((a, p) => a + p[0] * p[0], 0);
        const denom = (n * sumXX - sumX * sumX);
        if (denom === 0) return null;
        const m = (n * sumXY - sumX * sumY) / denom;
        const c = (sumY - m * sumX) / n;
        return { m, c };
    };

    const currentReg = useMemo(() => calculateRegression(points), [points]);
    const initialReg = useMemo(() => calculateRegression(initialPoints), [initialPoints]);

    const handleGraphClick = (point) => {
        const [x, y] = point;
        if (x >= 0 && x <= 10 && y >= 0 && y <= 10) {
            setPoints(prev => [...prev, [x, y]]);
        }
    };

    return (
        <div className="flexColumn gap-medium width-100">
            <div className="ch-emph-block relative" style={{ height: 400, overflow: 'hidden' }}>
                <div className="absolute top-2 left-2 z-10 ch-card padding-x--s padding-y--xs shadow-sm flexAlignCentre gap-small">
                    <PlusCircleOutlined style={{ color: '#1890ff' }} />
                    <span className="ch-text-small text-muted font-bold">Click graph to add outliers</span>
                </div>
                <Mafs onClick={handleGraphClick} viewBox={{ x: [0, 10], y: [0, 10] }} pan={false}>
                    <Coordinates.Cartesian xAxis={{ lines: true }} yAxis={{ lines: true }} />
                    {points.map((p, i) => (
                        <Point key={`${i}-${p[0]}`} x={p[0]} y={p[1]} color={Theme.indigo} opacity={0.8} />
                    ))}

                    {/* Initial "Ghost" Line */}
                    {showGhost && initialReg && (
                        <Plot.OfX y={x => initialReg.m * x + initialReg.c} color={Theme.gray} style="dashed" weight={2} opacity={0.5} />
                    )}

                    {/* Current Line */}
                    {currentReg && (
                        <g>
                            <Plot.OfX y={x => currentReg.m * x + currentReg.c} color={Theme.pink} weight={3} opacity={0.8} />
                            <Text x={5} y={currentReg.m * 5 + currentReg.c + 0.5} color={Theme.pink} weight="bold">
                                y = {currentReg.m.toFixed(2)}x + {currentReg.c.toFixed(2)}
                            </Text>
                        </g>
                    )}
                </Mafs>
            </div>
            <div className="ch-card padding--m bg-white border-all--s flexSpaceBetween flexAlignCentre">
                <div className="flexAlignCentre gap-medium">
                    <span className="ch-text-small text-muted">Show Original Trend</span>
                    <Switch size="small" checked={showGhost} onChange={setShowGhost} />
                </div>
                <Tooltip title="Reset">
                    <Button size="small" icon={<ReloadOutlined />} onClick={() => setPoints(initialPoints)}>Reset</Button>
                </Tooltip>
            </div>
        </div>
    );
};